/**
 * OWA-1018 and OWA-1083 (plan "Ordering and pending saves", items 1 and 4): the store side of this
 * tab's own saves, shared by every generated reducer.
 *
 * - A save is stamped after the version the tab holds for its record (the row shown, the latest
 *   accepted version kept behind a pending row, or the recorded deletion), by raising the tab's
 *   counter to it first. An edit made from the version the tab holds therefore becomes current
 *   under the server's rule (the newest addedOn wins) even when this tab's clock is behind, and
 *   stamps stay unique and in dispatch order within the tab.
 * - The save is shown when it is dispatched, marked pending, before its saga waits to queue it.
 *   A save cloned from the store in the same tick then carries the first one's changes.
 * - No incoming version (fetch, socket, another tab) replaces a pending row. It is kept as the
 *   latest accepted version instead.
 * - The mark belongs to one operation (its queue key) and only that operation's outcome settles
 *   it: acknowledged, the record shows the answer (or a newer accepted version); refused, failed
 *   to queue, or dropped from the queue, it returns to the latest accepted version. A later edit
 *   takes the mark over, and an earlier operation's outcome then only updates the accepted
 *   version behind it.
 *
 * The mark lives beside the rows (ArrayMap pending saves), never on an entity: it is not sent,
 * not written to the backup, and does not survive a reload.
 */

import type ArrayMap from './ArrayMap';
import type {PendingSave} from './ArrayMap';
import type {EntityType} from '../@types/entity';
import newHRTimestamp from './picos/timestamp';
import {readHRCounter} from './picos/timestampCounter';
import {genUUID} from './picos/uuids';
import {isHeldDocumentStatus} from './recoverableErrors';

type Row = {id: string, addedOn: number, status?: string | null};
/** An accepted version as the reducer applies it: a row, a deleted row, or a backup deletion record. */
type Version = NonNullable<PendingSave['accepted']>;
/** The entity reducer's acceptance rule (applyAccepted): `sentAddedOn` lets the row it names be replaced. */
export type Apply = (st: ArrayMap<any>, version: any, sentAddedOn?: number) => boolean;

/** What a settled mark leaves for the backup: rows to write, or a full rewrite (a record that never existed). */
export type BackupWrite = {rows: any[], full: boolean};

const finite = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/** The newest stamp this tab holds for a record. */
export function heldAddedOn(st: ArrayMap<any>, id: string): number {
    const pending = st.pendingSave(id);
    return Math.max(finite(st.get(id)?.addedOn), finite(pending?.accepted?.addedOn), finite(st.deletedVersion(id)));
}

/**
 * OWA-1083: stamp a dispatched save after the version the tab holds, and after every stamp this
 * tab has given, so the queue (run by addedOn) keeps dispatch order across records too. A
 * caller's stamp that already satisfies both (it is the counter's latest) is kept. Changes `row`,
 * the dispatched object, so the saga queues the same stamp.
 */
export function stampAfterHeld(st: ArrayMap<any>, row: Row): void {
    const held = heldAddedOn(st, row.id);
    const stamp = finite(row.addedOn);
    if (stamp > held && stamp >= readHRCounter()) newHRTimestamp(stamp);
    else row.addedOn = newHRTimestamp(Math.max(held, stamp));
}

/**
 * OWA-1083: a remove or a restore is stamped by its saga; raise the counter first so that stamp
 * follows the version the tab holds.
 */
export function raiseClockToHeld(st: ArrayMap<any>, id: string): void {
    newHRTimestamp(heldAddedOn(st, id));
}

/** Whether `version` is newer than `than` under the acceptance rule (an equal deletion wins over an active version). */
function newer(version: Version, than: Version | null): boolean {
    if (!than) return true;
    if (version.addedOn !== than.addedOn) return version.addedOn > than.addedOn;
    return version.status === 'D' && than.status !== 'D';
}

/** The newer of two versions; `first` on a tie. */
const newest = (first: Version | null, second: Version | null): Version | null =>
    !first ? second : !second ? first : newer(second, first) ? second : first;

/**
 * Show a dispatched save, marked pending. A save of a record that is already pending takes the
 * mark over and keeps the accepted version behind it. False when the row was not shown.
 */
export function beginPending(st: ArrayMap<any>, row: Row, apply: Apply): boolean {
    const prior = st.pendingSave(row.id);
    const shown = st.get(row.id) ?? null;
    const deletedAt = st.deletedVersion(row.id);
    if (!apply(st, row, shown?.addedOn)) return false;
    st.setPendingSave(row.id, prior
        ? {key: row.addedOn, accepted: prior.accepted, deletedAt: prior.deletedAt}
        : {key: row.addedOn, accepted: shown, deletedAt});
    return true;
}

/**
 * The saga queued a pending save: its queued copy (stamped again if the queue key was taken)
 * replaces the row shown at dispatch. 'superseded' when a later save of this tab holds the row,
 * 'unmarked' when the record has no pending save.
 */
export function showQueued(st: ArrayMap<any>, row: Row, dispatchedAddedOn: number, queuedAt: number | undefined, apply: Apply): 'shown' | 'superseded' | 'unmarked' {
    const entry = st.pendingSave(row.id);
    if (!entry) return 'unmarked';
    if (entry.key !== dispatchedAddedOn) return 'superseded';
    apply(st, row, entry.key);
    st.setPendingSave(row.id, {...entry, key: row.addedOn, queuedAt: queuedAt ?? performance.now()});
    return 'shown';
}

/**
 * An incoming version of a pending record is kept as its latest accepted version; the pending row
 * stays. False when the record has no pending save.
 */
export function keepBehindPending(st: ArrayMap<any>, id: string, version: Version): boolean {
    const entry = st.pendingSave(id);
    if (!entry) return false;
    const baseline = entry.accepted ?? (entry.deletedAt !== undefined ? {addedOn: entry.deletedAt, status: 'D'} : null);
    if (newer(version, baseline)) st.setPendingSave(id, {...entry, accepted: version});
    return true;
}

/** A local remove or restore of the record supersedes its pending save. */
export function supersedePending(st: ArrayMap<any>, id: string): void {
    st.clearPendingSave(id);
}

export type Outcome =
    /** Acknowledged: the answer, or a newer accepted version */
    | {kind: 'accepted', answer: Version}
    /** Refused, failed to queue, or refused for good in the queue: the latest accepted version, or `current` when newer */
    | {kind: 'refused', current?: Version | null}
    /** Left the queue without this tab hearing why (another tab sent it, it was discarded, or a
     * later save superseded it there): the row returns to the latest accepted version, which
     * replaces it even when its stamp is lower (the server may have lowered a future stamp) */
    | {kind: 'released'}
    /** A refused document, whose row may hold the only copy of the user's work: the row stays, and
     * the latest accepted version applies under the acceptance rule */
    | {kind: 'kept'};

/**
 * Settle the pending save `key` of record `id`. Returns what the backup must take, or null when
 * `key` does not hold the record's mark (no mark, or a later save holds it).
 */
export function settlePending(st: ArrayMap<any>, id: string, key: number | undefined, outcome: Outcome, apply: Apply): BackupWrite | null {
    const entry = st.pendingSave(id);
    if (!entry || key === undefined || entry.key !== key) return null;
    st.clearPendingSave(id);
    const row = st.get(id);

    if (outcome.kind === 'kept') {
        return entry.accepted && apply(st, entry.accepted) ? {rows: [entry.accepted], full: false} : {rows: [], full: false};
    }

    const target = newest(outcome.kind === 'accepted' ? outcome.answer : outcome.kind === 'refused' ? outcome.current ?? null : null, entry.accepted);
    if (!target) {
        // Another tab may have sent this create. Keep it visible until the release fetch answers.
        if (outcome.kind === 'released' && row) {
            const retained = Object.assign(Object.create(Object.getPrototypeOf(row)), row, {persisted: true});
            apply(st, retained, row.addedOn);
            return {rows: [retained], full: false};
        }
        // A record this tab created and the server never accepted
        if (row) st.delete(row);
        if (entry.deletedAt !== undefined) st.recordDeletion(id, entry.deletedAt);
        return {rows: [], full: true};
    }
    apply(st, target, row?.addedOn);
    return {rows: [{...target, staleReplaces: key}], full: false};
}

/**
 * processQueue's check of the shared queue (every tab, every poll while rows are pending): a
 * queued save whose row has left the queue is released; one refused for good (held, a 409, or
 * out of retries) is refused, or kept for a document. A save queued after the queue was read is
 * left alone. Null (and no copy of the collection) when no pending save is settled.
 */
export function checkPendingSaves<T extends EntityType>(state: ArrayMap<T>, live: number[], refused: number[], readAt: number,
                                                        apply: Apply, keepRefusedRows: boolean): {st: ArrayMap<T>, write: BackupWrite} | null {
    const liveKeys = new Set(live), refusedKeys = new Set(refused);
    const due = state.pendingSaveEntries()
        .filter(([, entry]) => entry.queuedAt !== undefined && entry.queuedAt < readAt && !liveKeys.has(entry.key));
    if (!due.length) return null;

    const st = state.clone();
    const write: BackupWrite = {rows: [], full: false};
    for (const [id, entry] of due) {
        const outcome: Outcome = !refusedKeys.has(entry.key) ? {kind: 'released'} : keepRefusedRows ? {kind: 'kept'} : {kind: 'refused'};
        const settled = settlePending(st, id, entry.key, outcome, apply);
        if (!settled) continue;
        write.rows.push(...settled.rows);
        write.full ||= settled.full;
    }
    return {st, write};
}

/**
 * The refusals a save never recovers from in the queue (processQueue leaves the row held for the
 * user): a 409 conflict, or a document's held statuses.
 */
export function isPermanentRefusal(entityName: string, status: unknown): boolean {
    const code = Number(status);
    return code === 409 || (entityName === 'document' && isHeldDocumentStatus(code));
}

/** The statuses for which rest/api drops a save of any entity but a document from the queue. */
const DROPPED_SAVE_STATUSES: ReadonlySet<number> = new Set([400, 403, 404, 405, 406, 409, 411, 413, 414, 415, 416, 422]);

/**
 * Whether rest/api drops a save with this status from the queue instead of retrying it. A
 * document is never dropped: its row may hold the only copy of the user's work.
 */
export function isDroppedSave(entityName: string, status: unknown): boolean {
    return entityName !== 'document' && DROPPED_SAVE_STATUSES.has(Number(status));
}

/**
 * OWA-1018 (plan item 4, "any permanent fatalError"): a refusal that settles a save's pending
 * mark: one the queue holds for the user, or one rest/api drops.
 */
export function isTerminalRefusal(entityName: string, status: unknown): boolean {
    return isPermanentRefusal(entityName, status) || isDroppedSave(entityName, status);
}

/**
 * OWA-1083: give a row about to be queued an operation id, unless it has one (a task save's
 * lineage). The queue never lets one operation's row replace another's that holds the same key,
 * which two tabs stamping after one held row can produce. The id is not enumerable, so the
 * caller's object (which may be an editor's working copy) does not change shape; the queue writes
 * it with the row.
 */
export function ensureOperationId(row: object): void {
    if ((row as {opId?: string}).opId) return;
    Object.defineProperty(row, 'opId', {value: genUUID(), writable: true, configurable: true, enumerable: false});
}

/**
 * The request body of a queued save of any entity but a task (rest/staleTaskSaves taskWire): the
 * row without its queue operation id, which a row read straight from the queue carries as an
 * ordinary field. Other rows are sent as they are.
 */
export function wireRow<T>(row: T): T {
    if (!row || typeof row !== 'object' || !Object.prototype.propertyIsEnumerable.call(row, 'opId')) return row;
    const copy = Object.assign(Object.create(Object.getPrototypeOf(row)), row);
    delete copy.opId;
    return copy;
}

/** A document row may hold the only copy of the user's work: a refused document keeps its row. */
export const keepsRefusedRows = (entityName: string): boolean => entityName === 'document';

export type {PendingSave};
